import { useQueryClient } from '@tanstack/react-query';
import { Gallery } from 'iconsax-react';
import { isNotNil, pickBy } from 'rambda';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'usehooks-ts';
import Flyout from '@/components/common/Flyout';
import { FlyoutMenuItem } from '@/components/common/Flyout/item';
import useFileSizeLimit from '@/hooks/files/use-file-size-limit';
import { usePickMediaFile } from '@/hooks/files/use-pick-media-file';
import useUploadFile from '@/hooks/files/use-upload-file';
import { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import useUpdateGroupDetails from '@/hooks/groups/use-update-group-details';
import useUserPlatform, { useIsDesktop } from '@/hooks/use-user-platform';
import { convertFileToUploadable } from '@/models/uploadable';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { addToast } from '@/stores/toast';
import { readableFileSize } from '@/utils/file-size';
import { FileType } from '@/utils/file-type';
import { generateSessionKey } from '@/utils/session';

type Props = {
  groupId: number;
};

export function GroupImageUploadButton({ groupId }: Props) {
  const { isIOS } = useUserPlatform();
  const [show, , setShow] = useToggle();
  const { ref, pickMedia } = usePickMediaFile();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { upload, isLoading: isUploading } = useUploadFile();
  const { mutateAsync: updateGroup, isPending: isUpdating } = useUpdateGroupDetails(groupId);
  const { updateSession } = useConversationSessionStore();
  const { getFileSizeLimit } = useFileSizeLimit();
  const isDesktop = useIsDesktop();

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className='absolute bottom-0 right-0 flex size-6 items-center justify-center rounded-full bg-white p-1 shadow-lg'>
      <button
        type='button'
        disabled={isUploading || isUpdating}
        className='group relative'
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();

          isIOS || isDesktop ? pickMedia(undefined, FileType.Image) : setShow(true);
        }}>
        <div className='group-disabled:blur'>
          <Gallery className='size-4 text-secondary' />
        </div>

        <span className='loading loading-spinner loading-sm absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 text-secondary group-disabled:inline' />
      </button>

      <input
        type='file'
        className='sr-only'
        ref={ref}
        value=''
        onChange={async (e) => {
          if (e.target.files?.[0]) {
            const limit = getFileSizeLimit('image');
            if (limit && e.target.files[0].size > limit) {
              addToast({
                type: 'error',
                message: t('errors.file-size-limit', { limit: readableFileSize(limit) }),
              });
              return Promise.reject('FILE_TOO_LARGE');
            }
            const uploadResult = await upload(convertFileToUploadable(e.target.files?.[0]));
            const result = await updateGroup(
              pickBy(isNotNil, {
                GroupImg: uploadResult.FileUrl,
              }),
            );
            e.target.value = '';

            if (result.Code === 0) {
              updateSession(generateSessionKey('group', groupId), {
                GroupImg: uploadResult.FileUrl,
              });
              queryClient.invalidateQueries(generateGroupInfoQueryOptions(groupId)).catch(console.warn);
            }
          }
          setShow(false);
        }}
      />

      <Flyout
        show={show}
        onClose={() => setShow(false)}>
        <div className='flex flex-col items-stretch gap-2'>
          <div className='join join-vertical gap-y-px rounded-system-button'>
            {(['camera', 'album'] as const).map((item) => (
              <FlyoutMenuItem
                joined
                key={item}
                label={t(`upload.${item}`)}
                onClick={() => pickMedia(item, FileType.Image)}
              />
            ))}
          </div>

          <FlyoutMenuItem
            className='mt-4 text-secondary'
            label={t('common.cancel')}
            onClick={() => setShow(false)}
          />
        </div>
      </Flyout>
    </div>
  );
}
